import { faColumns, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { Dropdown, Form, Pagination, Table } from "react-bootstrap";

import MISTooltip from "../../../../components/tooltip";
import { DataMatchPair } from "../../../../models/DataMatchResult";
import { smartCompare } from "../../../../app/common/helper/SortUtils";
import {
    extractIncomingColumns,
    getCentralColumnValue,
    getProviderColumnValue,
    misCentralColumns,
    type ColumnOption,
} from "./column-options";
import {
    CENTRAL_COLUMN_HEADER,
    initVisibility,
    matchedPairKey,
    matchesNameSearch,
    type MatchedSortState,
    type SortState,
} from "./data-match-helpers";

// ─── SortIndicator (shared across table headers) ────────────────────────────────

export function SortIndicator({ sort, column }: { sort: SortState; column: string }) {
    const active = sort?.column === column;
    return (
        <span className={`data-match-sort-arrows${active ? ' data-match-sort-arrows--active' : ''}`}>
            <span className={`data-match-sort-arrow-up${active && sort.direction === 'asc' ? ' data-match-sort-arrow--on' : ''}`} />
            <span className={`data-match-sort-arrow-down${active && sort.direction === 'desc' ? ' data-match-sort-arrow--on' : ''}`} />
        </span>
    );
}

// ─── MatchedRow ─────────────────────────────────────────────────────────────────

const MatchedRow = memo(function MatchedRow({
    pair,
    originalIndex,
    centralCols,
    providerCols,
    onUnmatch,
}: {
    pair: DataMatchPair;
    originalIndex: number;
    centralCols: ColumnOption[];
    providerCols: ColumnOption[];
    onUnmatch: (index: number) => void;
}) {
    return (
        <tr>
            {centralCols.map((c) => {
                const val = getCentralColumnValue(c.value, pair.central);
                return <td key={c.value} title={val}>{val}</td>;
            })}
            {providerCols.map((c) => {
                const val = getProviderColumnValue(c.value, pair.provider);
                return <td key={c.value} title={val}>{val}</td>;
            })}
            <td className="data-match-matched-td-unmatch text-center text-nowrap">
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onUnmatch(originalIndex)}>
                    Unmatch
                </button>
            </td>
        </tr>
    );
});

// ─── MatchedRecordsSection ──────────────────────────────────────────────────────

const MatchedRecordsSection = memo(function MatchedRecordsSection({
    pairs,
    matchedCount,
    providerName,
    onUnmatch,
    onUndo,
    canUndo,
}: {
    pairs: DataMatchPair[];
    matchedCount: number;
    providerName: string;
    onUnmatch: (index: number) => void;
    onUndo: () => void;
    canUndo: boolean;
}) {
    const providerCols = extractIncomingColumns;
    const [visCentral, setVisCentral] = useState<Record<string, boolean>>(() => initVisibility(misCentralColumns));
    const [visProvider, setVisProvider] = useState<Record<string, boolean>>(() => initVisibility(extractIncomingColumns));

    const activeCentral = useMemo(
        () => misCentralColumns.filter((c) => visCentral[c.value] !== false),
        [visCentral]
    );
    const activeProvider = useMemo(
        () => providerCols.filter((c) => visProvider[c.value] !== false),
        [providerCols, visProvider]
    );

    const nCentral = activeCentral.length;
    const nProvider = activeProvider.length;

    const setCentral = (key: string, v: boolean) => {
        setVisCentral((prev) => {
            if (!v && Object.values(prev).filter(Boolean).length <= 1) return prev;
            return { ...prev, [key]: v };
        });
    };
    const setProvider = (key: string, v: boolean) => {
        setVisProvider((prev) => {
            if (!v && Object.values(prev).filter(Boolean).length <= 1) return prev;
            return { ...prev, [key]: v };
        });
    };

    const [matchedSort, setMatchedSort] = useState<MatchedSortState>(null);
    const [searchText, setSearchText] = useState('');

    const toggleMatchedSort = (side: 'central' | 'provider', column: string) => {
        setMatchedSort((prev) => {
            if (prev?.side === side && prev?.column === column) {
                return prev.direction === 'asc' ? { side, column, direction: 'desc' } : null;
            }
            return { side, column, direction: 'asc' };
        });
    };

    const filteredIndexedPairs = useMemo(() => {
        const indexed = pairs.map((pair, idx) => ({ pair, originalIndex: idx }));
        if (!searchText.trim()) return indexed;
        const term = searchText.trim();
        return indexed.filter(({ pair }) =>
            matchesNameSearch(pair.central, term, getCentralColumnValue) ||
            matchesNameSearch(pair.provider, term, getProviderColumnValue)
        );
    }, [pairs, searchText]);

    const sortedIndexedPairs = useMemo(() => {
        if (!matchedSort) return filteredIndexedPairs;
        const decorated = filteredIndexedPairs.map((item) => ({
            ...item,
            sortKey: matchedSort.side === 'central'
                ? getCentralColumnValue(matchedSort.column, item.pair.central)
                : getProviderColumnValue(matchedSort.column, item.pair.provider),
        }));
        decorated.sort((a, b) => {
            const cmp = smartCompare(a.sortKey, b.sortKey);
            return matchedSort.direction === 'asc' ? cmp : -cmp;
        });
        return decorated;
    }, [filteredIndexedPairs, matchedSort]);

    const deferredPairs = useDeferredValue(sortedIndexedPairs);

    const PAGE_SIZE = 100;
    const [page, setPage] = useState(0);
    const matchedScrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => { setPage(0); }, [matchedSort, searchText]);
    useEffect(() => { matchedScrollRef.current?.scrollTo(0, 0); }, [page]);
    const totalPages = Math.ceil(deferredPairs.length / PAGE_SIZE);
    const pagedPairs = useMemo(
        () => deferredPairs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
        [deferredPairs, page]
    );

    return (
        <div className="data-match-panel data-match-panel--matched" role="region" aria-label="Matched records">
            <div className="data-match-panel__head data-match-panel__head--split border-start-0">
                <span>
                    Matched records ({matchedCount} in this run)
                </span>
                <div className="data-match-matched-head-actions d-flex align-items-center gap-2 flex-nowrap">
                    <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Search by name…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="data-match-search-input"
                        aria-label="Search matched records by name"
                    />
                    <Dropdown autoClose="outside" align="end">
                        <Dropdown.Toggle
                            type="button"
                            variant="light"
                            size="sm"
                            className="data-match-cols-btn border text-nowrap"
                            id="dropdown-mis-central-cols"
                            title="MIS Central columns"
                        >
                            <FontAwesomeIcon icon={faColumns} className="me-1" />
                            <span className="data-match-cols-btn-label">Central</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="data-match-column-picker-menu shadow-sm">
                            {misCentralColumns.map((c) => (
                                <div key={c.value} className="px-3 py-1">
                                    <Form.Check
                                        type="checkbox"
                                        id={`mccol-${c.value}`}
                                        label={CENTRAL_COLUMN_HEADER[c.value] ?? c.label}
                                        checked={visCentral[c.value] !== false}
                                        onChange={(e) => setCentral(c.value, e.target.checked)}
                                    />
                                </div>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown autoClose="outside" align="end">
                        <Dropdown.Toggle
                            type="button"
                            variant="light"
                            size="sm"
                            className="data-match-cols-btn border text-nowrap"
                            id="dropdown-provider-cols"
                            title="Provider columns"
                        >
                            <FontAwesomeIcon icon={faColumns} className="me-1" />
                            <span className="data-match-cols-btn-label">Provider</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="data-match-column-picker-menu shadow-sm">
                            {providerCols.map((c) => (
                                <div key={c.value} className="px-3 py-1">
                                    <Form.Check
                                        type="checkbox"
                                        id={`prcol-${c.value}`}
                                        label={c.label}
                                        checked={visProvider[c.value] !== false}
                                        onChange={(e) => setProvider(c.value, e.target.checked)}
                                    />
                                </div>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <MISTooltip message="Undo last match" placement="bottom">
                        <button
                            type="button"
                            className="btn btn-light btn-sm data-match-cols-btn border data-match-undo-last-btn"
                            onClick={onUndo}
                            disabled={!canUndo}
                            aria-label="Undo last match"
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                        </button>
                    </MISTooltip>
                </div>
            </div>
            <div className="data-match-panel__body p-0">
                <div
                    ref={matchedScrollRef}
                    className="data-match-table-scroll data-match-table-scroll--matched data-match-matched-table-wrap"
                >
                    <Table striped size="sm" className="mb-0 data-match-matched-table">
                        <thead>
                            <tr>
                                {nCentral > 0 && (
                                    <th
                                        colSpan={nCentral}
                                        className="data-match-matched-th-group data-match-matched-th-group--central text-center"
                                        scope="colgroup"
                                    >
                                        MIS Central
                                    </th>
                                )}
                                {nProvider > 0 && (
                                    <th
                                        colSpan={nProvider}
                                        className="data-match-matched-th-group data-match-matched-th-group--provider text-center"
                                        scope="colgroup"
                                    >
                                        {providerName}
                                    </th>
                                )}
                                <th rowSpan={2} className="data-match-matched-th-unmatch text-center align-middle" scope="col">
                                    Unmatch
                                </th>
                            </tr>
                            <tr>
                                {activeCentral.map((c) => (
                                    <th
                                        key={c.value}
                                        scope="col"
                                        className="data-match-matched-th-sub data-match-matched-th-sub--central small data-match-sortable-th"
                                        onClick={() => toggleMatchedSort('central', c.value)}
                                    >
                                        <span className="data-match-th-label" title={CENTRAL_COLUMN_HEADER[c.value] ?? c.label}>{CENTRAL_COLUMN_HEADER[c.value] ?? c.label}</span>
                                        <SortIndicator
                                            sort={matchedSort?.side === 'central' ? matchedSort : null}
                                            column={c.value}
                                        />
                                    </th>
                                ))}
                                {activeProvider.map((c) => (
                                    <th
                                        key={c.value}
                                        scope="col"
                                        className="data-match-matched-th-sub data-match-matched-th-sub--provider small data-match-sortable-th"
                                        onClick={() => toggleMatchedSort('provider', c.value)}
                                    >
                                        <span className="data-match-th-label" title={c.label}>{c.label}</span>
                                        <SortIndicator
                                            sort={matchedSort?.side === 'provider' ? matchedSort : null}
                                            column={c.value}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pagedPairs.map(({ pair, originalIndex }) => (
                                <MatchedRow
                                    key={matchedPairKey(pair, originalIndex)}
                                    pair={pair}
                                    originalIndex={originalIndex}
                                    centralCols={activeCentral}
                                    providerCols={activeProvider}
                                    onUnmatch={onUnmatch}
                                />
                            ))}
                        </tbody>
                    </Table>
                </div>
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top" style={{ fontSize: '0.8125rem' }}>
                        <span className="text-muted">
                            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, deferredPairs.length)} of {deferredPairs.length}
                        </span>
                        <Pagination size="sm" className="mb-0">
                            <Pagination.Prev onClick={() => setPage((p) => p - 1)} disabled={page === 0} />
                            <Pagination.Next onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1} />
                        </Pagination>
                    </div>
                )}
            </div>
        </div>
    );
});

export default MatchedRecordsSection;
